import {
  browserTracingIntegration,
  init,
  reactRouterV6BrowserTracingIntegration,
  thirdPartyErrorFilterIntegration,
} from "@sentry/react";
import type { Integration } from "@sentry/types";
import { useEffect } from "react";

import {
  matchRoutes,
  createRoutesFromChildren,
  useNavigationType,
  useLocation,
} from "@shared/react-router-dom/react-router-dom";
import {
  isSandbox,
  isProductionBundle,
  isProduction,
  isMasterBranch,
} from "@shared/utils/fullscriptEnv/fullscriptEnv";

import { beforeSend } from "./beforeSend/beforeSend";

interface Props {
  enableRouterInstrumentation?: boolean;
  tracesSampleRate?: number;
}

const getIntegrations = (enableRouterInstrumentation: boolean): Integration[] => {
  const integrations: Integration[] = [];
  // Allows oauth, unsubscribe etc to work
  // Required to support SPAs that don't use ReactRouter
  if (!enableRouterInstrumentation) {
    integrations.push(browserTracingIntegration());
  } else {
    integrations.push(
      reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        stripBasename: false,
      })
    );
  }

  if (isMasterBranch()) {
    integrations.push(
      thirdPartyErrorFilterIntegration({
        filterKeys: ["hw-admin-js"],
        behaviour: "apply-tag-if-exclusively-contains-third-party-frames",
      })
    );
  }

  return integrations;
};

const environment = () => {
  if (isProduction()) {
    return "production";
  } else if (isSandbox()) {
    return "sandbox";
  } else if (process.env.FORCE_ENABLE_SENTRY === "true") {
    return "development";
  } else {
    return "staging";
  }
};

const initSentry = ({ enableRouterInstrumentation, tracesSampleRate }: Props = {}) => {
  init({
    attachStacktrace: true,
    dsn: "https://b1e6050ac9de430b807ad68a57ea5816@o927579.ingest.sentry.io/5878763",
    tunnel: "/api/sentry/reports",
    integrations: getIntegrations(enableRouterInstrumentation ?? false),
    tracesSampleRate: isProduction() ? tracesSampleRate || 1.0 : 0.0,
    enabled: process.env.FORCE_ENABLE_SENTRY === "true" || isProductionBundle(),
    environment: environment(),
    release: process.env.CI_COMMIT_SHA,
    denyUrls: [
      // browser extensions
      /extension:\/\//gi,
      /^file:\/\//gi,
      /webkit-masked-url:/i,
    ],
    beforeSend,
  });
};

export { initSentry };
